import { Box, IconButton, Popover, Stack } from "@mui/material";
import React, { useState } from "react";

import { StrokeColorIcon, StrokeWidthIcon } from "assets/icons";
import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { Typography } from "new-components/typography/Typography";

import { ANNOTATION_COLORS } from "../constants";

/**
 * Stroke width in pixels.
 */
export enum StrokeWidth {
  THIN = 1,
  NORMAL = 2,
  WIDE = 3,
}

const STROKE_WIDTHS = [StrokeWidth.THIN, StrokeWidth.NORMAL, StrokeWidth.WIDE];

type StrokeSettingsProps = {
  color?: string;
  onColorChange?: (color: string) => void;
  width?: StrokeWidth;
  onWidthChange?: (width: StrokeWidth) => void;
};

export function StrokeSettings({ width, onWidthChange, color, onColorChange }: StrokeSettingsProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);

  const currentStrokeWidth = width ? (width * 2).toString() : "2";

  return (
    <>
      <GroundlightButton
        variant="plain"
        iconColor={color}
        iconStrokeWidth={width?.toString()}
        endIcon={StrokeColorIcon}
        onClick={(event) => {
          setMenuAnchor(event.currentTarget);
        }}
      />

      <Popover
        open={menuAnchor !== null}
        anchorEl={menuAnchor}
        onClose={() => {
          setMenuAnchor(null);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: -15,
        }}
        PaperProps={{
          sx: {
            p: "10px",
            borderRadius: "5px",
            background: "#E5E9ED",
            display: "flex",
            flexDirection: "column",
            gap: "5px",
          },
        }}
      >
        <Box sx={{ display: "flex", gap: "5px" }}>
          <Typography>Stroke size:</Typography>
          <Stack direction="row" gap="17px">
            {STROKE_WIDTHS.map((strokeWidth) => {
              const isSelected = strokeWidth === width;

              return (
                <IconButton
                  sx={{
                    width: "28px",
                    height: "28px",
                    borderRadius: 0,
                    background: isSelected ? "#C8CDD3" : undefined,
                  }}
                  onClick={() => {
                    onWidthChange?.(strokeWidth);
                  }}
                >
                  <StrokeWidthIcon color={color} strokeWidth={(strokeWidth * 2).toString()} />
                </IconButton>
              );
            })}
          </Stack>
        </Box>

        <Box>
          <Typography>Stroke color:</Typography>
          <Stack direction="row" gap="17px">
            {ANNOTATION_COLORS.map((annotationColor) => {
              const isSelected = annotationColor === color;

              return (
                <IconButton
                  sx={{
                    width: "32px",
                    height: "32px",
                    borderRadius: 0,
                    background: isSelected ? "#C8CDD3" : undefined,
                  }}
                  onClick={() => {
                    onColorChange?.(annotationColor);
                  }}
                >
                  <StrokeColorIcon color={annotationColor} strokeWidth={currentStrokeWidth} />
                </IconButton>
              );
            })}
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
